import React, { useEffect, useState } from "react";
import adminRolesApi from "src/configs/apis/admin-roles";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AdminRolesPage from "./adminRoles.page";
import {
  AdminRoleProvider,
  useAdminRoleContext,
} from "../../contexts/adminRole.context";
import AdminAccessRightDialog from "../../components/dialog/access-right-dialog";
import CreateAdminRoleDialog from "../../components/dialog/createAdminRoleDialog";
import { addAlert } from "../../../notification";
import AccessRights from "../../../../configs/constants/accessRights";

function AdminRoles() {
  const [adminRoles, setAdminRoles] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [totalAdminRoles, setTotalAdminRoles] = useState(0);
  const [adminRolesPages, setAdminRolesPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemPerPage, setItemPerPage] = useState(25);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canView = useSelector((state) => state.session.accessRights.includes(AccessRights.ADMIN_VIEW));
  const canEdit = useSelector((state) => state.session.accessRights.includes(AccessRights.ADMIN_EDIT));

  useEffect(() => {
    if (!canView) {
      navigate("/admin");
    }
  }, [canView]);

  const {
    setActionOpen,
    setAccessRightOpen,
    setAccessRights,
    selectedAdminRole,
    setIsLoading,
  } = useAdminRoleContext();
  const handleActionOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setActionOpen(true);
  };

  const getAdminAccessRight = async () => {
    setIsLoading(true);
    adminRolesApi
      .getAccessRight({ uuid: selectedAdminRole.uuid })
      .then((data) => {
        setAccessRights(data.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAccessRightOpen = async () => {
    getAdminAccessRight();
    setAccessRightOpen(true);
  };

  const handleItemPerPageChange = (value) => {
    setItemPerPage(value);
  };

  const onPageClick = async (page) => {
    setIsLoading(true);
    setCurrentPage(page);
    adminRolesApi
      .getListing({ page, length: itemPerPage, name: "", status: "" })
      .then((data) => {
        const adminRoleResponse = data.data;
        setAdminRoles(adminRoleResponse.data);
        setTotalAdminRoles(adminRoleResponse.recordsTotal);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    adminRolesApi
      .getListing({ page: currentPage, length: 10, name: "", status: "" })
      .then((data) => {
        const adminRoleResponse = data.data;
        setAdminRoles(adminRoleResponse.data);
        setTotalAdminRoles(adminRoleResponse.recordsTotal);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const onCreateClick = () => {
    setOpenCreateDialog(true);
  };

  const handleCreateSubmit = (values) => {
    adminRolesApi.createRole({
      name: values.name,
    }).then(unwrapResult)
      .then(() => {
        dispatch(
          addAlert({
            severity: "success",
            message: "Access rights updated successfully",
          })
        );

        setIsLoading(true);
        setCurrentPage(0);
        adminRolesApi
          .getListing({ page: 0, length: itemPerPage, name: "", status: "" })
          .then((data) => {
            const adminRoleResponse = data.data;
            setAdminRoles(adminRoleResponse.data);
            setTotalAdminRoles(adminRoleResponse.recordsTotal);
          })
          .finally(() => {
            setIsLoading(false);
          });

        setOpenCreateDialog(false);
      })
      .catch((error) => {
        dispatch(
          addAlert({
            severity: "error",
            message: error.message,
          })
        );
        setIsLoading(false);
        setOpenCreateDialog(false);
      });
  };

  const handleUpdateSubmitted = () => {
    setIsLoading(true);
    adminRolesApi
      .getListing({ page: currentPage, length: itemPerPage, name: "", status: "" })
      .then((data) => {
        const adminRoleResponse = data.data;
        setAdminRoles(adminRoleResponse.data);
        setTotalAdminRoles(adminRoleResponse.recordsTotal);
      })
      .finally(() => {
        setIsLoading(false);
        setAccessRightOpen(false);
      });
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActionOpen(false);
  };

  return (
    <>
      <AdminRolesPage
        isCompaniesLoading={false}
        adminRoles={adminRoles}
        totalAdminRoles={totalAdminRoles}
        adminRolesPages={adminRolesPages}
        itemPerPage={itemPerPage}
        handleActionOpen={handleActionOpen}
        handleAccessRightOpen={handleAccessRightOpen}
        anchorEl={anchorEl}
        onPageClick={onPageClick}
        handleItemPerPageChange={handleItemPerPageChange}
        currentPage={currentPage}
        onCreateClick={onCreateClick}
        canEdit={canEdit}
        handleClose={handleClose}
      />
      <AdminAccessRightDialog
        handleSubmitted={handleUpdateSubmitted}
      />
      <CreateAdminRoleDialog
        open={openCreateDialog}
        handleClose={() => setOpenCreateDialog(false)}
        handleSubmit={handleCreateSubmit}
      />
    </>
  );
}

function AdminRolesContainer() {
  return (
    <AdminRoleProvider>
      <AdminRoles />
    </AdminRoleProvider>
  );
}

export default AdminRolesContainer;
